import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "alert-bandeau"
}

import { inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { createAnimation, IonContent, IonPage, IonRouterOutlet } from '@ionic/vue'
import { Device } from '@capacitor/device'
import { Network } from '@capacitor/network'
import { useCatalogStore } from "@/store/catalog"
import { storeToRefs } from "pinia"
import { useSettingsStore } from "@/store/settings"
import { useNoticesStore } from "@/store/notices"

// injections

export default /*@__PURE__*/_defineComponent({
  __name: 'defaultLayout',
  setup(__props) {

const $file = inject<any>('$file')
const $db = inject<any>('$db')

const settingsStore = useSettingsStore()
const { choicesAxeptio, online, isMobile, profile, isApp } = storeToRefs(settingsStore)

const route = useRoute()
// keep $route name in template without changing it
const $route = route

const { fetchUniverses, fetchCategories, fetchProducts } = useCatalogStore()
const { universes, categories, products } = storeToRefs(useCatalogStore())
const noticesStore = useNoticesStore()
const { notices } = storeToRefs(noticesStore)

// state refs
const memoryAlerte = ref(false)
const timeout = ref<ReturnType<typeof setTimeout> | null>(null)
const customAnimation = ref(false)
const idDevice = ref<any>(null)
const networkListener = ref<any>(null)

// watchers
watch(
  () => route.name,
  (to, from) => {
    customAnimation.value = to === 'profil-choice' && from !== 'welcome'
  }
)

// functions
async function fetchNetworkStatus() {
  const status = await Network.getStatus()
  online.value = status.connected
}

async function fetchDeviceInfo() {
  idDevice.value = await Device.getInfo()
  await settingsStore.platformDetection(idDevice.value)
}

function updateDeviceDetection() {
  const screenOrientation: any = (screen as any).orientation
  const type = screenOrientation?.type || null
  const orientation = type?.startsWith('portrait') ? 'portrait' : 'landscape'
  try {
    settingsStore.deviceDetection({
      width: window.innerWidth,
      orientation
    })
  } catch (e) {
    console.log(e)
  }
}

function handleResizeAndOrientation() {
  if (timeout.value) clearTimeout(timeout.value)
  timeout.value = setTimeout(() => updateDeviceDetection(), 1000)
}

function animation($event: any, $secondEvent: any) {
  return createAnimation()
    .addElement($secondEvent.enteringEl)
    .duration(500)
    .fromTo('transform', 'rotateY(0)', 'rotateY(180deg)')
    .fromTo('opacity', '1', '0')
    .addElement($secondEvent.leavingEl)
    .duration(500)
    .fromTo('transform', 'rotateY(180deg)', 'rotateY(0)')
    .fromTo('opacity', '0', '1')
}

function addMediaIfNew(url: any, listMedia: Array<{ type: string; url: any }>) {
  if (!url) return
  const exists = listMedia.some((m) => m.url === url)
  if (!exists) {
    listMedia.push({ type: 'IMG', url })
  }
}

function getCoverRecursive(item: any, listMedia: Array<{ type: string; url: any }>) {
  if (!item) return
  // Cover image
  addMediaIfNew(item?.cover?.url, listMedia)
  // Products recursion
  if (Array.isArray(item?.products) && item.products.length > 0) {
    const productIds = new Set(item.products.map((p: any) => p.id))
    const prods = products.value.filter((product: any) => productIds.has(product.id))
    prods.forEach((product: any) => {
      getCoverRecursive(product, listMedia)
    })
  }
  // Caracteristics images
  const caracteristics: any = item?.caracteristics
  if (caracteristics) {
    const images: any[] = (Object.values(caracteristics) as any)?.filter(
      (caract: any) =>
        (caract.technical_name.match(/image/) || caract.technical_name.match(/cover/)) &&
        !caract.technical_name.match(/image_argument/) &&
        !caract.technical_name.match(/image_mini/)
    ) as any
    images.forEach((image: any) => addMediaIfNew(image?.value?.url, listMedia))
    // explicit cover/manufacturer
    addMediaIfNew(caracteristics?.cover?.value?.url, listMedia)
    addMediaIfNew(caracteristics?.manufacturer?.value?.url, listMedia)
  }
}

async function fetchData(withMedia = false) {
  if (online.value) {
    await Promise.all([
      fetchUniverses(),
      fetchCategories(),
      fetchProducts(),
      noticesStore.fetchNotices()
    ])

    if (isApp.value) {
      await Promise.all([
        $db?.truncateTable('PRODUCT'),
        $db?.truncateTable('CATEGORY'),
        $db?.truncateTable('NOTICE'),
        $db?.truncateTable('UNIVERSE')
      ])
      await Promise.all([
        $db?.addProductsListOffline(products.value),
        $db?.addCategoriesListOffline(categories.value),
        $db?.addNoticesListOffline(notices.value),
        $db?.addUniversesListOffline(universes.value)
      ])

      // télécharger les images de l'arbo produits
      if (withMedia) {
        const listMedia: Array<{ type: string; url: any }> = []

        if (categories.value.length > 0) {
          categories.value.forEach((item: any) => {
            getCoverRecursive(item, listMedia)
          })
        }
        $file?.storeMedia(listMedia)
      }
      // verification de l'espace mémoire
      await alertMemory()
    }
  } else if (isApp.value) {
      const data = await Promise.all([
        $db?.getAllProducts(),
        $db?.getAllCategories(),
        $db?.getAllUniverses(),
        $db?.getAllNotices()
      ])

      products.value = data[0]
      categories.value = data[1]
      universes.value = data[2]
      notices.value = data[3]
  }
}

async function alertMemory() {
  try {
    if (isMobile.value) {
      const memory = await $file?.getInfoMemory()
      const min = (process as any)?.env?.VUE_APP_ALERTE_MIN_MEMORY_SIZE
      // affiche l'alerte mémoire si l'espace disque est inférieur à la variable ALERTE_MIN_MEMORY_SIZE
      if (memory?.freeDisk && typeof memory?.freeDisk == 'number' && memory?.freeDisk > 0 && min && memory?.freeDisk < min) {
        memoryAlerte.value = true
        return true
      }
    }
  } catch (error) {
    memoryAlerte.value = false
  }
  memoryAlerte.value = false
}

function setConfigAxeptio() {
  const _axcb: any[] = (window as any)._axcb || []
  _axcb.push((sdk: any) => {
    sdk.on('cookies:complete', (choices: any) => {
      choicesAxeptio.value = { ...choices }
    })
    sdk.on(
      'close',
      (event: any) => {
        if (!event)
          setTimeout(() => {
            sdk.openCookies()
          }, 500)
      },
      { replay: false }
    )
  })
  ;(window as any)._axcb = _axcb
}

// lifecycle
onMounted(async () => {
  window.addEventListener('resize', handleResizeAndOrientation)
  updateDeviceDetection()
  const promises = [fetchNetworkStatus(), fetchDeviceInfo()]
  await Promise.all(promises)

  networkListener.value = await Network.addListener('networkStatusChange', (status) => {
    online.value = status.connected
  })
  await fetchData(true)
  setConfigAxeptio()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResizeAndOrientation)
  if (timeout.value) {
    clearTimeout(timeout.value)
    timeout.value = null
  }
  networkListener.value && networkListener.value.remove && networkListener.value.remove()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([_unref(profile), "layout"])
  }, [
    _createVNode(_unref(IonPage), null, {
      default: _withCtx(() => [
        _createVNode(_unref(IonContent), null, {
          default: _withCtx(() => [
            (_openBlock(), _createBlock(_unref(IonRouterOutlet), {
              key: _unref($route).path,
              ref: "router",
              animation: customAnimation.value && _unref(isMobile) ? animation:undefined
            }, null, 8, ["animation"]))
          ]),
          _: 1
        }),
        (memoryAlerte.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, _cache[0] || (_cache[0] = [
              _createElementVNode("div", null, "Mémoire insuffisante pour télécharger tous les médias en hors connexion.", -1)
            ])))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 2))
}
}

})